import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateFineScheduleEntryDto,
  DecideTicketDisputeDto,
  DisputeTicketDto,
  FileTicketOptions,
  FineScheduleEntry,
  IssueTicketDto,
  RecordTicketPaymentDto,
  SendTicketToCollectionsDto,
  ServeTicketDto,
  TicketAvailability,
  TicketLgSummary,
  TicketListItem,
  TicketStatus,
  TicketView,
  UpdateFineScheduleEntryDto,
  VoidTicketDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';

/**
 * Tickets: the file panel, the Tickets screen and the fine schedule. Every
 * route is scoped server-side — file routes by the file, ticket routes through
 * the ticket's file, lists by the caller's `ticket:read` reach.
 */
export const ticketKeys = {
  all: ['tickets'] as const,
  byFile: (fileId: string) => ['tickets', 'file', fileId] as const,
  options: (fileId: string) => ['tickets', 'options', fileId] as const,
  one: (id: string) => ['tickets', 'one', id] as const,
  list: (lgId: string, status: string) => ['tickets', 'list', lgId, status] as const,
  summary: ['tickets', 'summary'] as const,
  availability: ['tickets', 'availability'] as const,
  schedule: (lgId: string) => ['tickets', 'schedule', lgId] as const,
};

export function useTicketAvailability(enabled: boolean) {
  return useQuery({
    queryKey: ticketKeys.availability,
    queryFn: async () => (await api.get<TicketAvailability>('/tickets/availability')).data,
    enabled,
    // A plan change is rare; the screen does not need to notice within seconds.
    staleTime: 5 * 60_000,
  });
}

export function useFileTickets(fileId: string) {
  return useQuery({
    queryKey: ticketKeys.byFile(fileId),
    queryFn: async () => (await api.get<TicketListItem[]>(`/files/${fileId}/tickets`)).data,
    enabled: !!fileId,
    retry: false,
  });
}

export function useTicketOptions(fileId: string, enabled = true) {
  return useQuery({
    queryKey: ticketKeys.options(fileId),
    queryFn: async () =>
      (await api.get<FileTicketOptions>(`/files/${fileId}/tickets/options`)).data,
    enabled: !!fileId && enabled,
    retry: false,
  });
}

export function useTickets(localGovernmentId: string | undefined, status: TicketStatus[]) {
  const statusKey = status.join(',');
  return useQuery({
    queryKey: ticketKeys.list(localGovernmentId ?? '', statusKey),
    queryFn: async () =>
      (
        await api.get<TicketListItem[]>('/tickets', {
          params: { localGovernmentId, status: statusKey || undefined },
        })
      ).data,
    enabled: !!localGovernmentId,
  });
}

export function useTicketSummary(enabled = true) {
  return useQuery({
    queryKey: ticketKeys.summary,
    queryFn: async () => (await api.get<TicketLgSummary[]>('/tickets/summary')).data,
    enabled,
  });
}

export function useFineSchedule(localGovernmentId: string | undefined) {
  return useQuery({
    queryKey: ticketKeys.schedule(localGovernmentId ?? ''),
    queryFn: async () =>
      (await api.get<FineScheduleEntry[]>(`/local-governments/${localGovernmentId}/fine-schedule`))
        .data,
    enabled: !!localGovernmentId,
  });
}

export function useIssueTicket(fileId: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (dto: IssueTicketDto) =>
      (await api.post<TicketView>(`/files/${fileId}/tickets`, dto)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success('gov.tickets.issuedToast');
    },
    onError: (err) => toast.error(err, 'gov.tickets.issueError'),
  });
}

/** Every move after issue, in one hook: they share invalidation and feedback. */
export type TicketAction =
  | { kind: 'serve'; dto: ServeTicketDto }
  | { kind: 'dispute'; dto: DisputeTicketDto }
  | { kind: 'decide'; dto: DecideTicketDisputeDto }
  | { kind: 'void'; dto: VoidTicketDto }
  | { kind: 'collections'; dto: SendTicketToCollectionsDto }
  | { kind: 'payment'; dto: RecordTicketPaymentDto };

const ACTION_PATH: Record<TicketAction['kind'], string> = {
  serve: 'serve',
  dispute: 'dispute',
  decide: 'dispute/decision',
  void: 'void',
  collections: 'collections',
  payment: 'payments',
};

export function useTicketAction() {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: TicketAction }) =>
      (await api.post<TicketView>(`/tickets/${id}/${ACTION_PATH[action.kind]}`, action.dto)).data,
    onSuccess: (data, { action }) => {
      qc.setQueryData(ticketKeys.one(data.id), data);
      qc.invalidateQueries({ queryKey: ticketKeys.all });
      toast.success(`gov.tickets.actionToast.${action.kind}`);
    },
    onError: (err) => toast.error(err, 'gov.tickets.actionError'),
  });
}

export function useSaveFineScheduleEntry(localGovernmentId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: async (
      input:
        | { id?: undefined; dto: CreateFineScheduleEntryDto }
        | { id: string; dto: UpdateFineScheduleEntryDto },
    ) => {
      const base = `/local-governments/${localGovernmentId}/fine-schedule`;
      return input.id
        ? (await api.patch<FineScheduleEntry>(`${base}/${input.id}`, input.dto)).data
        : (await api.post<FineScheduleEntry>(base, input.dto)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ticketKeys.schedule(localGovernmentId ?? '') });
      toast.success('gov.tickets.schedule.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.tickets.schedule.saveError'),
  });
}

/** Download the printed ticket as Word or PDF. */
export async function downloadTicketDocument(
  ticket: { id: string; reference: string },
  format: 'docx' | 'pdf',
): Promise<void> {
  const res = await api.get(`/tickets/${ticket.id}/document`, {
    params: { format },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${ticket.reference}.${format}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Integer cents as the reader's locale prints money. */
export function formatMoney(cents: number, currency: string, language: string): string {
  return new Intl.NumberFormat(language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

/** The offence in the reader's language. */
export function offenceLabel(
  t: { offenceEn: string; offenceFr: string },
  language: string,
): string {
  return language.startsWith('fr') ? t.offenceFr : t.offenceEn;
}

/** Which follow-up moves the panel offers for a ticket in this status. */
export function ticketActions(t: {
  status: TicketStatus;
  amountPaidCents: number;
}): TicketAction['kind'][] {
  switch (t.status) {
    case 'issued':
      return ['serve', 'payment', ...(t.amountPaidCents === 0 ? (['void'] as const) : [])];
    case 'served':
      return ['payment', 'dispute', ...(t.amountPaidCents === 0 ? (['void'] as const) : [])];
    case 'overdue':
      return [
        'payment',
        'dispute',
        'collections',
        ...(t.amountPaidCents === 0 ? (['void'] as const) : []),
      ];
    case 'disputed':
      return ['decide', 'payment'];
    case 'collections':
      return ['payment'];
    default:
      return [];
  }
}
